
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Post {
  id: string;
  title: string;
  content: string;
  verdict: string;
  author: string;
  category: string;
  featured: boolean;
  published: boolean;
  rating: number;
  cover_image: string;
  bestseller: boolean;
  our_pick: boolean;
  created_at: string;
  updated_at: string;
}

interface Category {
  id: string;
  name: string;
  description: string;
}

interface PostEditorProps {
  post: Post | null;
  onSave: () => void;
  onCancel: () => void;
}

export default function PostEditor({ post, onSave, onCancel }: PostEditorProps) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [verdict, setVerdict] = useState('');
  const [author, setAuthor] = useState('');
  const [category, setCategory] = useState('Fiction Review');
  const [genre, setGenre] = useState('Literary Fiction');
  const [featured, setFeatured] = useState(false);
  const [published, setPublished] = useState(false);
  const [bestseller, setBestseller] = useState(false);
  const [ourPick, setOurPick] = useState(false);
  const [rating, setRating] = useState(0);
  const [coverImage, setCoverImage] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const genres = [
    'Literary Fiction',
    'Historical Fiction',
    'Mystery',
    'Thriller',
    'Crime Fiction',
    'Suspense',
    'Psychological Fiction',
    'Drama',
    'Adventure',
    'Satire',
    'Humor',
    'Family Saga',
    'Coming-of-Age',
    'Women\'s Fiction',
    'Urban Fiction',
    'Political Fiction',
    'War Fiction',
    'Dystopian Fiction',
    'Utopian Fiction',
    'Experimental Fiction',
    'Science Fiction',
    'Fantasy',
    'Horror',
    'Contemporary Romance',
    'Historical Romance',
    'Romantic Comedy',
    'LGBTQ+ Romance',
    'Cozy Mystery',
    'True Crime',
    'LGBTQ+ Mystery',
    'Non-Fiction',
    'Memoir',
    'History',
    'Poetry',
    'Religion/Spirituality',
    'Self Help'
  ];

  useEffect(() => {
    fetchCategories();
    if (post) {
      setTitle(post.title);
      setContent(post.content);
      setVerdict(post.verdict || '');
      setAuthor(post.author);
      setCategory(post.category);
      setGenre((post as any).genre || 'Literary Fiction');
      setFeatured(post.featured);
      setPublished(post.published);
      setBestseller(post.bestseller || false);
      setOurPick(post.our_pick || false);
      setRating(post.rating || 0);
      setCoverImage(post.cover_image || '');
    }
  }, [post]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name');

      if (error) {
        console.error('Error fetching categories:', error);
      } else {
        setCategories(data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const postData = {
        title,
        content,
        verdict,
        author,
        category,
        genre,
        featured,
        published,
        bestseller,
        our_pick: ourPick,
        rating,
        cover_image: coverImage,
        updated_at: new Date().toISOString(),
      };

      if (post) {
        // Update existing post
        const { error } = await supabase
          .from('posts')
          .update(postData)
          .eq('id', post.id);

        if (error) {
          setError(error.message);
        } else {
          onSave();
        }
      } else {
        // Create new post
        const { error } = await supabase
          .from('posts')
          .insert([postData]);

        if (error) {
          setError(error.message);
        } else {
          onSave();
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = () => {
    const halfStarRatings = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5];
    
    return (
      <div className="space-y-3">
        <div className="flex items-center space-x-1">
          {[1, 2, 3, 4, 5].map((star) => {
            const isFullStar = rating >= star;
            const isHalfStar = rating >= star - 0.5 && rating < star;
            
            return (
              <div key={star} className="relative">
                <button
                  type="button"
                  onClick={() => setRating(star)}
                  className="w-8 h-8 flex items-center justify-center text-2xl transition-colors duration-200 hover:scale-110 cursor-pointer"
                >
                  <i
                    className={`ri-star-${isFullStar ? 'fill' : 'line'} ${
                      isFullStar ? 'text-yellow-400' : 'text-gray-300'
                    }`}
                  ></i>
                </button>
                {/* Half star overlay */}
                <button
                  type="button"
                  onClick={() => setRating(star - 0.5)}
                  className="absolute inset-0 w-4 h-8 flex items-center justify-center text-2xl transition-colors duration-200 hover:scale-110 cursor-pointer overflow-hidden"
                >
                  <i
                    className={`ri-star-half-fill ${
                      isHalfStar ? 'text-yellow-400' : 'text-transparent'
                    }`}
                  ></i>
                </button>
              </div>
            );
          })}
          <span className="ml-2 text-sm text-gray-600">
            {rating > 0 ? `${rating}/5 stars` : 'No rating'}
          </span>
        </div>
        
        {/* Quick rating buttons */}
        <div className="flex flex-wrap gap-2">
          {halfStarRatings.map((ratingValue) => (
            <button
              key={ratingValue}
              type="button"
              onClick={() => setRating(ratingValue)}
              className={`px-3 py-1 text-xs rounded-full border transition-colors cursor-pointer ${
                rating === ratingValue
                  ? 'bg-yellow-100 border-yellow-400 text-yellow-800'
                  : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
              }`}
            >
              {ratingValue}★
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {post ? 'Edit Post' : 'Create New Post'}
        </h3>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
              Title
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="author" className="block text-sm font-medium text-gray-700 mb-2">
              Author
            </label>
            <input
              type="text"
              id="author"
              name="author"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
              required
            />
          </div>

          <div>
            <label htmlFor="verdict" className="block text-sm font-medium text-gray-700 mb-2">
              Verdict
            </label>
            <textarea
              id="verdict"
              name="verdict"
              value={verdict}
              onChange={(e) => setVerdict(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Brief verdict or summary of your review..."
              required
            />
          </div>

          <div>
            <label htmlFor="cover-image" className="block text-sm font-medium text-gray-700">
              Cover Art URL
            </label>
            <input
              type="url"
              id="cover-image"
              value={coverImage}
              onChange={(e) => setCoverImage(e.target.value)}
              placeholder="https://example.com/book-cover.jpg"
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
            {coverImage && (
              <div className="mt-3">
                <p className="text-sm text-gray-600 mb-2">Cover Preview:</p>
                <img
                  src={coverImage}
                  alt="Cover preview"
                  className="w-32 h-48 object-cover rounded-lg shadow-md"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rating (Click stars or use quick buttons)
            </label>
            {renderStars()}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                Category
              </label>
              <div className="mt-1 relative">
                <select
                  id="category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="block w-full bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-8 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  {categories.length > 0 ? (
                    categories.map((cat) => (
                      <option key={cat.id} value={cat.name}>
                        {cat.name}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="Fiction Review">Fiction Review</option>
                      <option value="Non-Fiction Review">Non-Fiction Review</option>
                      <option value="Poetry Review">Poetry Review</option>
                      <option value="Literary Analysis">Literary Analysis</option>
                    </>
                  )}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="genre" className="block text-sm font-medium text-gray-700">
                Genre
              </label>
              <div className="mt-1 relative">
                <select
                  id="genre"
                  value={genre}
                  onChange={(e) => setGenre(e.target.value)}
                  className="block w-full bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-8 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  {genres.map((genreOption) => (
                    <option key={genreOption} value={genreOption}>
                      {genreOption}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700">
              Content
            </label>
            <textarea
              id="content"
              rows={12}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              required
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Write your review or article here..."
            />
          </div>

          <div className="flex items-center space-x-6">
            <div className="flex items-center">
              <input
                id="featured"
                type="checkbox"
                checked={featured}
                onChange={(e) => setFeatured(e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
              />
              <label htmlFor="featured" className="ml-2 block text-sm text-gray-900 cursor-pointer">
                Featured
              </label>
            </div>

            <div className="flex items-center">
              <input
                id="published"
                type="checkbox"
                checked={published}
                onChange={(e) => setPublished(e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
              />
              <label htmlFor="published" className="ml-2 block text-sm text-gray-900 cursor-pointer">
                Published
              </label>
            </div>

            <div className="flex items-center">
              <input
                id="bestseller"
                type="checkbox"
                checked={bestseller}
                onChange={(e) => setBestseller(e.target.checked)}
                className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded cursor-pointer"
              />
              <label htmlFor="bestseller" className="ml-2 block text-sm text-gray-900 cursor-pointer flex items-center">
                <i className="ri-award-line text-amber-500 mr-1"></i>
                Bestseller
              </label>
            </div>

            <div className="flex items-center">
              <input
                id="our-pick"
                type="checkbox"
                checked={ourPick}
                onChange={(e) => setOurPick(e.target.checked)}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded cursor-pointer"
              />
              <label htmlFor="our-pick" className="ml-2 block text-sm text-gray-900 cursor-pointer flex items-center">
                <span className="text-green-600 mr-1">🍁</span>
                Our Pick
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 whitespace-nowrap cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-indigo-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 whitespace-nowrap cursor-pointer"
            >
              {loading ? 'Saving...' : 'Save Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
